"use client";
import { createContext, useContext, useState, useEffect, useRef } from "react";
import FadeTransition from "@/components/ui/fade-transition";
import Image from "next/image";

// Safe logging wrapper
const safeLogger = {
  addBreadcrumb: (breadcrumb) => {
    console.log("🍞 [Loading Provider] Breadcrumb:", breadcrumb);
  },
  captureMessage: (message, options) => {
    console.log("📝 [Loading Provider]", message, options);
  },
};

const LoadingContext = createContext({
  isLoading: false,
  setIsLoading: () => {},
});

export function LoadingProvider({ children }) {
  const [isLoading, setIsLoading] = useState(false);
  const [hasTimedOut, setHasTimedOut] = useState(false);
  const timeoutRef = useRef(null);
  const loadingStartTimeRef = useRef(null);
  const authCheckIntervalRef = useRef(null);

  useEffect(() => {
    if (isLoading) {
      // Record when loading started
      loadingStartTimeRef.current = Date.now();

      // Start monitoring auth state during loading
      authCheckIntervalRef.current = setInterval(() => {
        // Add periodic breadcrumbs during loading to track auth state
        if (typeof window !== "undefined") {
          const currentLoadingTime = Date.now() - loadingStartTimeRef.current;

          // Only add breadcrumbs every 5 seconds to avoid spam
          if (currentLoadingTime % 5000 < 1000) {
            safeLogger.addBreadcrumb({
              message: "Loading state check",
              level: "info",
              data: {
                loadingDuration: currentLoadingTime,
                url: window.location.href,
                pathname: window.location.pathname,
                search: window.location.search,
                userAgent: window.navigator.userAgent,
                timestamp: new Date().toISOString(),
                // Check if we're on a callback URL which might indicate middleware issues
                isAuthCallback:
                  window.location.pathname.includes("/auth/callback"),
                hasAuthError: window.location.search.includes("auth_error"),
                // Check local storage for auth debugging
                hasSupabaseSession: !!window.localStorage.getItem(
                  "sb-" +
                    (process.env.NEXT_PUBLIC_SUPABASE_URL?.split(
                      "//"
                    )[1]?.split(".")[0] || "unknown") +
                    "-auth-token"
                ),
              },
            });
          }
        }
      }, 1000);

      // Single consolidated timeout that escalates based on duration
      timeoutRef.current = setTimeout(() => {
        const loadingDuration = Date.now() - loadingStartTimeRef.current;

        // Gather comprehensive debugging info
        const debugInfo = {
          loadingDuration: loadingDuration,
          loadingStartTime: new Date(loadingStartTimeRef.current).toISOString(),
          url: typeof window !== "undefined" ? window.location.href : "unknown",
          pathname:
            typeof window !== "undefined"
              ? window.location.pathname
              : "unknown",
          search:
            typeof window !== "undefined" ? window.location.search : "unknown",
          userAgent:
            typeof window !== "undefined"
              ? window.navigator.userAgent
              : "unknown",
          previousPage:
            typeof document !== "undefined" ? document.referrer : "unknown",
          timestamp: new Date().toISOString(),

          // Auth-specific debugging
          isAuthCallback:
            typeof window !== "undefined"
              ? window.location.pathname.includes("/auth/callback")
              : false,
          hasAuthError:
            typeof window !== "undefined"
              ? window.location.search.includes("auth_error")
              : false,
          authError:
            typeof window !== "undefined" &&
            window.location.search.includes("auth_error")
              ? new URLSearchParams(window.location.search).get("auth_error")
              : null,

          // Check for Supabase session in localStorage
          hasSupabaseSession:
            typeof window !== "undefined"
              ? !!window.localStorage.getItem(
                  "sb-" +
                    (process.env.NEXT_PUBLIC_SUPABASE_URL?.split(
                      "//"
                    )[1]?.split(".")[0] || "unknown") +
                    "-auth-token"
                )
              : false,

          // Browser state that might affect middleware
          isVisible:
            typeof document !== "undefined"
              ? document.visibilityState === "visible"
              : true,
          hasFocus:
            typeof document !== "undefined" ? document.hasFocus() : true,

          // Network connectivity
          isOnline: typeof navigator !== "undefined" ? navigator.onLine : true,

          // Potential middleware/routing issues
          isProtectedRoute:
            typeof window !== "undefined" &&
            (window.location.pathname.startsWith("/user") ||
              window.location.pathname.startsWith("/space")),
          isAuthRoute:
            typeof window !== "undefined" &&
            (window.location.pathname.startsWith("/signin") ||
              window.location.pathname.startsWith("/signup")),
          isPublicRoute:
            typeof window !== "undefined" &&
            ([
              "/",
              "/about",
              "/terms",
              "/privacy",
              "/featured",
              "/onboarding",
            ].includes(window.location.pathname) ||
              window.location.pathname.startsWith("/featured")),
        };

        // Log that user is stuck in loading state
        safeLogger.addBreadcrumb({
          message: "User stuck in loading state - detailed analysis",
          level: "warning",
          data: debugInfo,
        });

        // Determine likely cause based on URL and state
        let likelyCause = "unknown";
        if (debugInfo.isAuthCallback && debugInfo.hasAuthError) {
          likelyCause = "auth_callback_error";
        } else if (debugInfo.isAuthCallback) {
          likelyCause = "auth_callback_stuck";
        } else if (
          debugInfo.isProtectedRoute &&
          !debugInfo.hasSupabaseSession
        ) {
          likelyCause = "protected_route_no_session";
        } else if (debugInfo.isAuthRoute && debugInfo.hasSupabaseSession) {
          likelyCause = "auth_route_with_session";
        } else if (!debugInfo.isOnline) {
          likelyCause = "offline";
        } else if (!debugInfo.isVisible || !debugInfo.hasFocus) {
          likelyCause = "tab_visibility_issue";
        } else if (debugInfo.isProtectedRoute) {
          likelyCause = "protected_route_middleware_issue";
        } else {
          likelyCause = "general_auth_stuck";
        }

        safeLogger.captureMessage(
          "User stuck in loading state for more than 15 seconds",
          {
            level: "warning",
            tags: {
              component: "LoadingProvider",
              issue_type: "stuck_loading",
              likely_cause: likelyCause,
              route_type: debugInfo.isProtectedRoute
                ? "protected"
                : debugInfo.isAuthRoute
                  ? "auth"
                  : debugInfo.isPublicRoute
                    ? "public"
                    : "unknown",
              has_session: debugInfo.hasSupabaseSession ? "yes" : "no",
            },
            extra: debugInfo,
          }
        );

        // Enhanced console logging with color coding
        console.group(
          `%c🚨 User stuck in loading state for ${loadingDuration}ms`,
          "color: red; font-weight: bold;"
        );
        console.log(
          `%c🎯 Likely cause: ${likelyCause}`,
          "color: orange; font-weight: bold;"
        );
        console.log(`%c📍 URL: ${debugInfo.url}`, "color: blue;");
        console.log(
          `%c🔐 Has session: ${debugInfo.hasSupabaseSession}`,
          "color: purple;"
        );
        console.log(
          `%c🛣️ Route type: ${debugInfo.isProtectedRoute ? "protected" : debugInfo.isAuthRoute ? "auth" : "public"}`,
          "color: green;"
        );
        if (debugInfo.authError) {
          console.log(`%c❌ Auth error: ${debugInfo.authError}`, "color: red;");
        }
        console.log("Full debug info:", debugInfo);
        console.groupEnd();

        // If it's a middleware issue, try to help with additional context
        if (
          likelyCause.includes("middleware") ||
          likelyCause.includes("callback")
        ) {
          console.warn("💡 Middleware debugging tips:");
          console.warn(
            "- Check Network tab for any failed requests to Supabase"
          );
          console.warn("- Look for middleware redirects in Network tab");
          console.warn("- Check if cookies are being set properly");
          console.warn(
            "- Verify NEXT_PUBLIC_SUPABASE_URL and NEXT_PUBLIC_SUPABASE_ANON_KEY"
          );
        }

        // Schedule the final timeout to show error page after 1 minute total
        timeoutRef.current = setTimeout(() => {
          const finalLoadingDuration = Date.now() - loadingStartTimeRef.current;

          console.error(
            `🚨 Loading timed out after ${finalLoadingDuration}ms (1 minute)`
          );

          // Send Slack notification for timeout
          sendLoadingTimeoutNotification(finalLoadingDuration);

          // Show error state
          setHasTimedOut(true);
          setIsLoading(false);
        }, 45000); // Additional 45 seconds (15 + 45 = 60 seconds total)
      }, 15000); // Initial warning at 15 seconds
    } else {
      // Clear intervals and timeouts when loading ends
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }

      if (authCheckIntervalRef.current) {
        clearInterval(authCheckIntervalRef.current);
        authCheckIntervalRef.current = null;
      }

      // Log successful loading completion if it was previously active
      if (loadingStartTimeRef.current) {
        const loadingDuration = Date.now() - loadingStartTimeRef.current;

        // Only log if loading took a significant amount of time (>2 seconds) for context
        if (loadingDuration > 2000) {
          const completionInfo = {
            loadingDuration: loadingDuration,
            url:
              typeof window !== "undefined" ? window.location.href : "unknown",
            pathname:
              typeof window !== "undefined"
                ? window.location.pathname
                : "unknown",
            wasStuck: loadingDuration > 10000, // Was it potentially stuck?
          };

          safeLogger.addBreadcrumb({
            message: "Loading completed",
            level: "info",
            data: completionInfo,
          });

          // Log successful resolution if it was taking a long time
          if (loadingDuration > 10000) {
            console.log(
              `%c✅ Loading resolved after ${loadingDuration}ms`,
              "color: green; font-weight: bold;",
              completionInfo
            );
          }
        }

        loadingStartTimeRef.current = null;
      }
    }

    // Cleanup function
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      if (authCheckIntervalRef.current) {
        clearInterval(authCheckIntervalRef.current);
        authCheckIntervalRef.current = null;
      }
    };
  }, [isLoading]);

  // Function to send loading timeout notification
  const sendLoadingTimeoutNotification = async (loadingDuration) => {
    // DISABLED: Only signup notifications are enabled
    console.log("ℹ️ Skipping Slack notification for loading timeout (notifications disabled except signups)");
    return;
    try {
      console.log("🔔 [Loading Provider] Sending timeout notification");

      // Gather user information
      let userInfo = "Not authenticated";
      let userEmail = "N/A";
      let userId = "N/A";

      try {
        const qensaStore = localStorage.getItem("qensa-store");
        if (qensaStore) {
          const parsedStore = JSON.parse(qensaStore);
          if (parsedStore?.state?.session?.user) {
            const user = parsedStore.state.session.user;
            userEmail = user.email || "N/A";
            userId = user.id || "N/A";
            userInfo = `${userEmail} (ID: ${userId})`;
          }
        }
      } catch (userInfoError) {
        console.error("Failed to get user info:", userInfoError);
      }

      // Create error message for Slack
      const errorMessage = {
        text: "⏰ Loading Timeout Detected",
        blocks: [
          {
            type: "header",
            text: {
              type: "plain_text",
              text: "⏰ Loading Timeout Alert",
              emoji: true,
            },
          },
          {
            type: "section",
            fields: [
              {
                type: "mrkdwn",
                text: `*Issue:* Loading screen timeout`,
              },
              {
                type: "mrkdwn",
                text: `*Duration:* ${Math.round(loadingDuration / 1000)} seconds`,
              },
              {
                type: "mrkdwn",
                text: `*User:* ${userInfo}`,
              },
              {
                type: "mrkdwn",
                text: `*Time:* ${new Date().toISOString()}`,
              },
            ],
          },
          {
            type: "section",
            fields: [
              {
                type: "mrkdwn",
                text: `*URL:* ${window.location.href}`,
              },
              {
                type: "mrkdwn",
                text: `*User Agent:* ${navigator.userAgent.slice(0, 100)}...`,
              },
            ],
          },
          {
            type: "section",
            text: {
              type: "mrkdwn",
              text: `*Context:* User was stuck on loading screen for over 1 minute. This indicates a potential issue with authentication, middleware, or server response times.`,
            },
          },
        ],
      };

      const response = await fetch("/api/error-notification", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(errorMessage),
      });

      if (response.ok) {
        console.log(
          "✅ [Loading Provider] Timeout notification sent successfully"
        );
      } else {
        console.error(
          "❌ [Loading Provider] Failed to send timeout notification:",
          response.status
        );
      }
    } catch (error) {
      console.error(
        "❌ [Loading Provider] Error sending timeout notification:",
        error
      );
    }
  };

  // Function to handle retry
  const handleRetry = () => {
    setHasTimedOut(false);
    setIsLoading(true);
  };

  // Function to go home
  const handleGoHome = () => {
    window.location.href = "/";
  };

  // If timed out, show error page
  if (hasTimedOut) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center p-4 text-center bg-gradient-to-b from-red-700 to-gray-900">
        <Image
          src="/logos/beta/white.svg"
          alt="Qensa Logo"
          width={180}
          height={60}
          className="mb-8"
        />

        <h1 className="text-4xl font-bold mb-4 text-white">Loading Timeout</h1>

        <div className="bg-white/20 rounded-lg p-6 mb-6 max-w-lg backdrop-blur-sm">
          <div className="w-16 h-16 rounded-full bg-white/20 flex items-center justify-center mb-4 mx-auto">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-8 w-8 text-white"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
          </div>

          <h3 className="text-xl font-semibold text-white mb-2">
            Taking too long?
          </h3>

          <p className="text-white/80 mb-4">
            The page is taking longer than expected to load. This might be due
            to a temporary issue with our servers or your connection.
          </p>

          <p className="text-white/70 text-sm mb-4">
            Our team has been automatically notified of this issue.
          </p>

          <p className="text-white/70 text-sm mb-4">
            Need immediate help? Contact our support team:
          </p>

          <a
            href="mailto:contact@qensa.com"
            className="inline-block text-white font-medium hover:text-white/90 underline mb-4"
          >
            contact@qensa.com
          </a>
        </div>

        <div className="flex flex-col sm:flex-row gap-4">
          <button
            onClick={handleRetry}
            className="px-6 py-3 bg-white text-red-700 font-semibold rounded-lg hover:bg-white/90 transition-colors"
          >
            Try Again
          </button>
          <button
            onClick={handleGoHome}
            className="px-6 py-3 border-2 border-white text-white font-semibold rounded-lg hover:bg-white hover:text-red-700 transition-colors"
          >
            Go to Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <LoadingContext.Provider value={{ isLoading, setIsLoading }}>
      <FadeTransition show={isLoading}>
        <div className="fixed inset-0 flex items-center justify-center bg-white z-50">
          <div className="animate-pulse">
            <Image
              src="/logos/beta/purple.svg"
              alt="Qensa Logo"
              width={200}
              height={50}
              priority
            />
          </div>
        </div>
      </FadeTransition>
      {children}
    </LoadingContext.Provider>
  );
}

export const useLoading = () => useContext(LoadingContext);
